import {
  DataTableShell,
  tdClass,
  thClass,
} from "@/features/exports/components/DataTableShell";
import { formatCreditNoteMoney } from "@/domain/creditNotes/formatCreditNote";
import { creditNoteStatusLabel } from "@/domain/creditNotes/creditNoteStatus";
import { formatInvoiceDateTime } from "@/domain/invoices/formatInvoice";
import {
  customerEmailForId,
  customerNameForId,
} from "@/domain/customers/formatCustomer";
import { ClickCopyTextCell } from "@/features/invoices/components/ClickCopyTextCell";
import { CustomerEmailCell } from "@/features/invoices/components/CustomerEmailCell";
import { InvoiceIdCell } from "@/features/invoices/components/InvoiceIdCell";
import type { CreditNoteRow, CustomerRow } from "@/types/api";
import { cn } from "@/utils/cn";

interface CreditNotesTableProps {
  items: CreditNoteRow[];
  customers: CustomerRow[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onRowDownload?: (id: string, creditNoteNumber?: string | null) => void;
  downloadingId?: string | null;
  embedded?: boolean;
  fixedColumnLayout?: boolean;
}

const compactThClass = `${thClass} px-1.5 py-1.5 invoice-cell`;
const compactTdClass = `${tdClass} px-1.5 py-2 invoice-cell`;
const tightThClass = `${compactThClass} col-tight`;
const tightTdClass = `${compactTdClass} col-tight`;
const truncatedIdThClass = `${tightThClass} col-resource-id col-invoice-id`;
const truncatedIdTdClass = `${tightTdClass} col-resource-id col-invoice-id`;
const fillThClass = `${compactThClass} col-fill`;
const fillTdClass = `${compactTdClass} col-fill`;
const downloadThClass = `${tightThClass} col-download`;
const downloadTdClass = `${tightTdClass} col-download`;

function CreditNotesFixedColGroup() {
  return (
    <colgroup>
      <col className="invoice-col-check" />
      <col className="credit-note-col-resource" />
      <col className="credit-note-col-resource" />
      <col className="invoice-col-customer" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-amount" />
      <col className="invoice-col-status" />
      <col className="invoice-col-date" />
      <col className="invoice-col-download" />
    </colgroup>
  );
}

export function CreditNotesTable({
  items,
  customers,
  selected,
  onToggle,
  onRowDownload,
  downloadingId,
  embedded = false,
  fixedColumnLayout = false,
}: CreditNotesTableProps) {
  const table = (
    <table
      className={cn(
        "invoice-table-compact w-full border-collapse text-left text-sm",
        embedded && !fixedColumnLayout && "min-w-[800px]",
        fixedColumnLayout && "invoice-table-compact--fixed-layout invoice-table-compact--credit-notes",
      )}
    >
      {fixedColumnLayout ? <CreditNotesFixedColGroup /> : null}
          <thead>
            <tr>
              <th className={`${tightThClass} w-8`} />
              <th className={truncatedIdThClass}>Credit note</th>
              <th className={truncatedIdThClass}>Invoice number</th>
              <th className={fillThClass}>Customer</th>
              <th className={tightThClass}>Customer ID</th>
              <th className={fillThClass}>Email</th>
              <th className={tightThClass}>Amount</th>
              <th className={tightThClass}>Status</th>
              <th className={`${tightThClass} col-date`}>CN created</th>
              <th className={downloadThClass}>PDF</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const exportable =
                row.exportable !== false && row.status === "issued";
              const name =
                customerNameForId(row.customer_id, customers) ||
                row.customer_name ||
                row.customer_id;
              const email = customerEmailForId(row.customer_id, customers);
              const loading = downloadingId === row.id;

              return (
                <tr
                  key={row.id}
                  className={cn(!exportable && "opacity-[0.72]")}
                >
                  <td className={`${tightTdClass} w-8`}>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      disabled={!exportable}
                      title={
                        !exportable
                          ? "Void credit notes are not exportable"
                          : undefined
                      }
                      onChange={() => onToggle(row.id)}
                    />
                  </td>
                  <td className={truncatedIdTdClass}>
                    <InvoiceIdCell
                      id={row.id}
                      invoiceNumber={row.number}
                      resourceLabel="Credit note number"
                    />
                  </td>
                  <td className={truncatedIdTdClass}>
                    {row.invoice_id ? (
                      <InvoiceIdCell
                        id={row.invoice_id}
                        invoiceNumber={row.invoice_number}
                      />
                    ) : (
                      <span className="cell-meta">{"\u2014"}</span>
                    )}
                  </td>
                  <td className={fillTdClass}>
                    <ClickCopyTextCell
                      value={name}
                      resourceLabel="Customer name"
                      variant="name"
                    />
                  </td>
                  <td className={tightTdClass}>
                    <ClickCopyTextCell
                      value={row.customer_id}
                      resourceLabel="Customer ID"
                      variant="meta"
                      tabularNums
                    />
                  </td>
                  <td className={fillTdClass}>
                    <CustomerEmailCell email={email} />
                  </td>
                  <td className={`${tightTdClass} tabular-nums text-sm`}>
                    <span
                      className="block truncate"
                      title={formatCreditNoteMoney(row.amount, row.currency)}
                    >
                      {formatCreditNoteMoney(row.amount, row.currency)}
                    </span>
                  </td>
                  <td className={tightTdClass}>
                    <span
                      className={cn(
                        "chip",
                        row.status === "issued" && "chip-ok",
                      )}
                    >
                      {creditNoteStatusLabel(row.status)}
                    </span>
                  </td>
                  <td className={`${tightTdClass} col-date cell-date tabular-nums`}>
                    {formatInvoiceDateTime(row.created)}
                  </td>
                  <td className={downloadTdClass}>
                    {exportable && onRowDownload ? (
                      <button
                        type="button"
                        className={cn(
                          "btn-row-dl btn-table-action",
                          loading && "is-loading",
                        )}
                        disabled={loading}
                        aria-busy={loading || undefined}
                        aria-label={loading ? "PDF downloading" : "Download PDF"}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onRowDownload(row.id, row.number)}
                      >
                        {loading ? "..." : "Download"}
                      </button>
                    ) : (
                      <span className="cell-meta">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
  );

  return (
    <DataTableShell embedded={embedded}>
      {embedded ? table : <div className="overflow-x-auto">{table}</div>}
    </DataTableShell>
  );
}
